/**
 * The generation loop's progress protocol.
 *
 * Delivered over SSE rather than WebSockets: this is one-way server-to-client
 * for the lifetime of one job, which is exactly what SSE is, and it survives
 * proxies, needs no upgrade handshake, and reconnects on its own.
 */
import { z } from 'zod';
import { blueprintSchema } from './blueprint.js';
import { tokenUsageSchema } from './cost.js';
import { generationTypeSchema } from './queues.js';
/**
 * The pipeline's phases, in order.
 *
 * Exposed to the client so the UI can render a real sequence rather than a
 * spinner — the plan phase exists partly so there is something honest to show
 * while the expensive phase runs.
 */
export const GENERATION_PHASES = [
    'queued',
    'planning',
    /**
     * Edits only: deciding which files the instruction actually touches.
     *
     * It is a visible phase because it is the phase that makes an edit an edit.
     * "Working out what to change" is also the honest thing to show while the
     * model reads a site it did not just write.
     */
    'targeting',
    'generating',
    'validating',
    'repairing',
    'building',
    'publishing',
    'done',
    'failed',
];
export const generationPhaseSchema = z.enum(GENERATION_PHASES);
export const generationEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('phase'), phase: generationPhaseSchema, at: z.string() }),
    /** The plan, as soon as it validates. This is what makes the wait tolerable. */
    z.object({ type: z.literal('blueprint'), blueprint: blueprintSchema }),
    /** One per generated file, emitted as it lands rather than in a final batch. */
    z.object({
        type: z.literal('file'),
        path: z.string(),
        bytes: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('repair'),
        attempt: z.number().int().positive(),
        violations: z.array(z.string()),
    }),
    /**
     * Running totals, so cost is visible while it accrues rather than after.
     *
     * `model` rides along because a run against the deterministic fixture client
     * looks identical to a real one in every other respect — including a 100% build
     * rate. Anything reading this stream must be able to tell them apart.
     */
    z.object({
        type: z.literal('usage'),
        usage: tokenUsageSchema,
        costMicroUsd: z.number().int().nonnegative(),
        model: z.string(),
        live: z.boolean(),
    }),
    z.object({
        type: z.literal('done'),
        versionId: z.uuid(),
        previewUrl: z.string(),
        generationsCharged: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('failed'),
        code: z.string(),
        message: z.string(),
        /**
         * Whether this failure drew a site generation. It never does.
         *
         * Kept as a field rather than assumed, because the UI states the
         * consequence to the customer and must not state it from a guess. It is
         * named for what happened — not "refunded": nothing is reserved up front
         * under the pack model, so there is no reservation to give back, and a
         * contract that still talks about refunds describes a product we do not sell.
         */
        charged: z.boolean(),
    }),
]);
/** Redis Stream key carrying one generation's events. */
export const generationStreamKey = (generationId) => `gen:events:${generationId}`;
/**
 * Per-user concurrency lock.
 *
 * One generation at a time per user, per the technical task. Enforced with a
 * Redis key rather than a database row so the check costs no transaction, and
 * with a TTL so a worker that dies mid-job cannot lock the user out forever.
 */
export const userGenerationLockKey = (userId) => `gen:lock:${userId}`;
export const USER_GENERATION_LOCK_TTL_SECONDS = 15 * 60;
/** Events are kept briefly after completion so a late reconnect can catch up. */
export const GENERATION_STREAM_TTL_SECONDS = 60 * 60;
// ── HTTP contracts ───────────────────────────────────────────────────────────
export const createProjectSchema = z.object({
    /**
     * Three characters, not ten.
     *
     * Ten rejected "a plumber" — nine characters, and exactly the kind of thing a
     * real person types into a box that says "describe your site". The planner's
     * whole job is to turn a thin brief into a real one; refusing to let it try is
     * the product declining work it can do. The eval keeps that prompt precisely
     * because a generator that needs a good brief is not a product.
     */
    prompt: z.string().trim().min(3).max(4_000),
    /** Optional display name. Derived from the blueprint when absent. */
    name: z.string().trim().min(1).max(60).optional(),
});
export const editProjectSchema = z.object({
    prompt: z.string().trim().min(3).max(4_000),
    /**
     * The `data-gen-id` of the element the user clicked in the preview, if any.
     *
     * Scoping an edit to a section is the difference between "make it darker" and
     * "make *this* darker" — without it the model has to guess which of six
     * sections the user meant, and guessing wrong is what makes an editor feel
     * broken.
     */
    targetGenId: z.string().max(120).optional(),
    /** Edit from an older version, which is how restore-then-edit works. */
    fromVersionId: z.uuid().optional(),
});
export const projectSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    status: z.string(),
    previewUrl: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const generationSummarySchema = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    type: generationTypeSchema,
    status: z.string(),
    phase: generationPhaseSchema.nullable(),
    creditsReserved: z.number().int(),
    creditsCharged: z.number().int(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
    costMicroUsd: z.number().int(),
    error: z.string().nullable(),
    createdAt: z.string(),
});
export const versionSummarySchema = z.object({
    id: z.uuid(),
    parentVersionId: z.uuid().nullable(),
    /** The instruction that produced it — what makes a history list readable. */
    prompt: z.string(),
    model: z.string(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
    isCurrent: z.boolean(),
    createdAt: z.string(),
});
export const publishResponseSchema = z.object({
    url: z.string(),
    versionId: z.uuid(),
});
export const startGenerationResponseSchema = z.object({
    generationId: z.uuid(),
    projectId: z.uuid(),
    creditsReserved: z.number().int().nonnegative(),
});
/**
 * Job ids, constructed so the API can recover a generation from a queue event
 * without loading the payload.
 *
 * A hyphen, not a colon: BullMQ rejects `:` in a custom job id outright, and the
 * failure surfaces as a 500 at enqueue time rather than anywhere near the id.
 */
/**
 * Where a version's plan JSON lives in object storage.
 *
 * One helper rather than three string literals that have to agree: the generation
 * worker writes it, the API records it, and an edit reads its parent back out of
 * it. A typo in any one of them is a generation that cannot be edited.
 */
export const planSourceKey = (projectId, versionId) => `plans/${projectId}/${versionId}.json`;
export const generationJobId = (generationId) => `generate-${generationId}`;
export const buildJobId = (generationId) => `build-${generationId}`;
/** Inverse of the two above. Returns null when the prefix does not match. */
export const generationIdFromJobId = (jobId, kind) => jobId.startsWith(`${kind}-`) ? jobId.slice(kind.length + 1) : null;
//# sourceMappingURL=generation.js.map